import React from 'react'
import { Bullet } from 'components/atoms/icons'
import moment from 'moment-timezone'
import PropTypes from 'prop-types'
import { Button } from 'transfix-ui/components/Button'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { TrackingTag } from 'transfix-ui/components/TrackingTag'
import { formatDriverLastPing } from 'utils/formatDatetime'
import { isEtaStatus } from 'utils/mapbox'
import { FORMATS } from 'constants/datetime'
import { ARRIVED_STATUSES } from 'constants/shipments'
import { TrackingRequirements } from './TrackingRequirements'

export const ManualEtaInfo = React.memo(({ formattedManualEta, manualEta, stopType }) => {
  const { dateTime, lateDuration } = formattedManualEta
  return (
    <>
      <div className='flex items-center'>
        <span className='text-secondary'>Manual ETA to {stopType}</span>
      </div>
      {manualEta && (
        <div className='flex items-center'>
          <span>
            {dateTime} {lateDuration}&nbsp;
          </span>
        </div>
      )}
    </>
  )
})

export const AutoEta = React.memo(({ finalStopDistanceMiles, nextStopTimeStamp, stopType }) => (
  <>
    <div className='flex items-center'>
      <span className='text-secondary'>Auto ETA to {stopType}</span>
    </div>
    <div className='flex items-center'>
      <span>
        {nextStopTimeStamp}
        <Bullet />
        {finalStopDistanceMiles} to next stop
      </span>
    </div>
  </>
))

export const CheckedInAt = React.memo(({ previousStopIndex, stops }) => {
  const { dayFormat, timeFormat } = FORMATS
  const previousStop = stops[previousStopIndex]
  const {
    status: { arrivedAt, timezone },
  } = previousStop
  const arrivedAtDateTime = moment(arrivedAt).tz(timezone).format(`${dayFormat} ${timeFormat}`)
  return (
    <>
      <div className='flex items-center'>
        <span className='text-secondary'>Checked in at</span>
      </div>
      <div>
        <span>{arrivedAtDateTime}</span>
      </div>
    </>
  )
})

export const TrackingDetails = ({
  shipment,
  mapbox = {},
  openBookingDialog,
  showTrackingUpdateButton,
}) => {
  const {
    driverLocationTimezone,
    info: { formattedManualEta, formattedProject44Eta, previousStopIndex } = {},
    isDriverTracking,
    latestDriverLocationAt,
    status: { currentStopIndex, key: shipmentStatus } = {},
    stops,
    trackingStatus,
    project44Metadata,
    keepTruckinMetadata,
  } = shipment
  const { calculatedData: { finalStopDistanceMiles, nextStopTime } = {}, routes } = mapbox
  const nextStopTimeStamp = nextStopTime ? moment(nextStopTime).format(FORMATS.etaFormat) : ''
  const currentStop = stops[currentStopIndex]
  const { manualEta, project44Eta, stopSequence, type } = currentStop
  const stopType = `${type.charAt(0).toUpperCase()}${stopSequence}`
  const isArrivedAt = ARRIVED_STATUSES.includes(shipmentStatus)
  const showEta = isEtaStatus(shipmentStatus)
  const showAutoEta = routes.length > 0 && currentStop && showEta && !project44Eta

  let lastPing

  if (isDriverTracking) {
    const formattedDriverLastPing = formatDriverLastPing(
      driverLocationTimezone,
      latestDriverLocationAt
    )
    lastPing = `Last ping ${formattedDriverLastPing}`
  } else if (!isDriverTracking && latestDriverLocationAt === null) {
    lastPing = 'No pings yet'
  } else {
    const formattedDriverLastPing = formatDriverLastPing(
      driverLocationTimezone,
      latestDriverLocationAt
    )
    lastPing = `Last ping ${formattedDriverLastPing}`
  }
  const openDialog = () =>
    openBookingDialog({ id: 'update_status', statusUpdate: 'Tracking Update' })

  return (
    <>
      <section className='sidebar-body'>
        <h3 className='body-medium mb2'>Tracking</h3>
        <div className='mv2'>
          <TrackingRequirements />
          <div className='flex items-center pv2'>
            <TrackingTag trackingStatus={trackingStatus} />
            <span>{lastPing}</span>
          </div>
          {project44Metadata?.needsValidTruckNumber && (
            <InlineMessage
              type={INLINE_MESSAGE_VARIANTS.ALERT}
              message='Valid Project44 truck Number required for tracking with P44'
            />
          )}
          {keepTruckinMetadata?.needsValidTruckNumber && (
            <InlineMessage
              type={INLINE_MESSAGE_VARIANTS.ALERT}
              message='Valid KeepTruckin truck Number required for tracking with KT'
            />
          )}
        </div>
        <div className='row'>
          <div className='col-xs-5 col-xl-5'>
            {isArrivedAt && (
              <div className='mv1'>
                <CheckedInAt stops={stops} previousStopIndex={previousStopIndex} />
              </div>
            )}
            <ManualEtaInfo
              formattedManualEta={formattedManualEta}
              manualEta={manualEta}
              stopType={stopType}
            />
          </div>
        </div>
        {showAutoEta && (
          <AutoEta
            finalStopDistanceMiles={finalStopDistanceMiles}
            nextStopTimestamp={nextStopTimeStamp}
            stopType={stopType}
          />
        )}
        {project44Eta && (
          <>
            <div className='flex items-center'>
              <span className='text-secondary'>P44 ETA to {stopType}</span>
            </div>
            <div
              className={`flex items-center ${
                formattedProject44Eta.lateDuration ? 'text-alert' : ''
              }`}>
              <span>
                {formattedProject44Eta.dateTime} {formattedProject44Eta.lateDuration}
              </span>
            </div>
          </>
        )}
        {showTrackingUpdateButton && (
          <div className='mv2'>
            <Button data-test='tracking-update-button' onClick={openDialog} color='secondary'>
              Tracking Update
            </Button>
          </div>
        )}
      </section>
      <hr className='mv0' />
    </>
  )
}

TrackingDetails.propTypes = {
  shipment: PropTypes.shape({}).isRequired,
  mapbox: PropTypes.shape({}),
  showTrackingUpdateButton: PropTypes.bool.isRequired,
  openBookingDialog: PropTypes.func,
}

TrackingDetails.defaultProps = {
  mapbox: {},
  openBookingDialog: () => {},
}

CheckedInAt.propTypes = {
  stops: PropTypes.number.isRequired,
  previousStopIndex: PropTypes.number.isRequired,
}

ManualEtaInfo.propTypes = {
  formattedManualEta: PropTypes.shape({
    dateTime: PropTypes.string,
    lateDuration: PropTypes.string,
  }),
  manualEta: PropTypes.bool,
  stopType: PropTypes.string,
}

ManualEtaInfo.defaultProps = {
  formattedManualEta: {
    dateTime: '',
    lateDuration: '',
  },
  manualEta: false,
  stopType: '',
}

AutoEta.propTypes = {
  finalStopDistanceMiles: PropTypes.string,
  nextStopTimeStamp: PropTypes.string,
  stopType: PropTypes.string,
}

AutoEta.defaultProps = {
  finalStopDistanceMiles: '',
  nextStopTimeStamp: '',
  stopType: '',
}
